import React, { memo, useMemo } from 'react';
import { Row, Col, Card, Button, Space, Badge, Tooltip, Spin } from 'antd';
import { 
  ReloadOutlined, 
  SettingOutlined, 
  DownloadOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { usePerformance } from '../../contexts/PerformanceContext';
import MetricCard from './MetricCard';
import HealthStatus from './HealthStatus';
import SystemResources from './SystemResources';
import PerformanceCharts from './PerformanceCharts';
import PerformanceAlerts from './PerformanceAlerts';
import { formatBytes, formatDuration, getStatusColor } from '../../config/performanceDashboard';
import './PerformanceDashboard.css';

const PerformanceDashboard = memo(({ className = '', style = {} }) => {
  const {
    data,
    loading,
    errors,
    lastUpdated,
    isAutoRefresh,
    alertCount,
    performanceStatus,
    isHealthy,
    fetchAllData,
    setAutoRefresh,
    updateSettings,
  } = usePerformance();

  const { performance, health, resources } = data;

  // Handle refresh
  const handleRefresh = () => {
    fetchAllData();
  };

  // Handle auto-refresh toggle
  const handleToggleAutoRefresh = () => {
    setAutoRefresh(!isAutoRefresh);
  };

  // Handle settings update
  const handleSettingsUpdate = () => {
    // This would open a settings modal in a real implementation
    console.log('Open settings modal');
  };

  // Handle export data
  const handleExportData = () => {
    // This would trigger data export in a real implementation
    console.log('Export performance data');
  };

  // Performance metrics cards
  const performanceMetrics = useMemo(() => {
    if (!performance) return [];

    const { eventLoopLatency, memory, cpu, requests } = performance;

    return [
      {
        title: 'Event Loop Latency',
        value: eventLoopLatency?.current || 0,
        unit: 'ms',
        status: eventLoopLatency?.current > 100 ? 'critical' : 
                eventLoopLatency?.current > 50 ? 'warning' : 'healthy',
        tooltip: `P95: ${eventLoopLatency?.p95 || 0}ms, P99: ${eventLoopLatency?.p99 || 0}ms`,
        icon: <InfoCircleOutlined />,
      },
      {
        title: 'Process Heap Memory',
        value: memory?.processHeap?.percentage || 0,
        unit: '%',
        status: memory?.processHeap?.percentage > 80 ? 'critical' : 
                memory?.processHeap?.percentage > 70 ? 'warning' : 'healthy',
        tooltip: `Used: ${formatBytes(memory?.processHeap?.used || 0)} / ${formatBytes(memory?.processHeap?.total || 0)}`,
        icon: <InfoCircleOutlined />,
      },
      {
        title: 'System Memory',
        value: memory?.systemMemory?.percentage || 0,
        unit: '%',
        status: memory?.systemMemory?.percentage > 80 ? 'critical' : 
                memory?.systemMemory?.percentage > 70 ? 'warning' : 'healthy',
        tooltip: `Used: ${formatBytes(memory?.systemMemory?.used || 0)} / ${formatBytes(memory?.systemMemory?.total || 0)}`,
        icon: <InfoCircleOutlined />,
      },
      {
        title: 'CPU Usage',
        value: cpu?.usage || 0,
        unit: '%',
        status: cpu?.usage > 90 ? 'critical' : 
                cpu?.usage > 70 ? 'warning' : 'healthy',
        tooltip: `Load Average: ${cpu?.loadAverage?.map(l => l.toFixed(2)).join(', ') || 'N/A'}`,
        icon: <InfoCircleOutlined />,
      },
      {
        title: 'Request Success Rate',
        value: requests?.successRate || 0,
        unit: '%',
        status: requests?.successRate < 95 ? 'critical' : 
                requests?.successRate < 98 ? 'warning' : 'healthy',
        tooltip: `Total: ${requests?.total || 0}, Errors: ${requests?.errors || 0}`,
        icon: <InfoCircleOutlined />,
      },
      {
        title: 'Average Response Time',
        value: requests?.averageResponseTime || 0,
        unit: 'ms',
        status: requests?.averageResponseTime > 1000 ? 'critical' : 
                requests?.averageResponseTime > 500 ? 'warning' : 'healthy',
        tooltip: `Total requests: ${requests?.total || 0}`,
        icon: <InfoCircleOutlined />,
      },
    ];
  }, [performance]);

  // Loading state
  if (loading.all) {
    return (
      <div className={`performance-dashboard ${className}`} style={style}>
        <Card>
          <div className="performance-dashboard-loading">
            <Spin size="large" />
            <p>Loading performance data...</p>
          </div>
        </Card>
      </div>
    );
  }

  // Error state
  if (Object.values(errors).some(error => error !== null)) {
    const errorDetails = Object.entries(errors)
      .filter(([_, error]) => error !== null)
      .map(([type, error]) => `${type}: ${error?.message || error}`)
      .join(', ');

    return (
      <div className={`performance-dashboard ${className}`} style={style}>
        <Card>
          <div className="performance-dashboard-error">
            <h3>Performance Dashboard Error</h3>
            <p>Something went wrong while loading the performance dashboard.</p>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '6px' }}>
              <strong>Error Details:</strong>
              <pre style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#ff4d4f' }}>
                {errorDetails}
              </pre>
            </div>
            <Space>
              <Button type="primary" onClick={handleRefresh}>
                <ReloadOutlined />
                Refresh
              </Button>
              <Button onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </Space>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={`performance-dashboard ${className}`} style={style}>
      {/* Header */}
      <div className="performance-dashboard-header">
        <div className="performance-dashboard-title">
          <h2>Performance Dashboard</h2>
          <div className="performance-dashboard-status">
            <Badge 
              color={getStatusColor(performanceStatus)} 
              text={performanceStatus.toUpperCase()}
            />
            {alertCount > 0 && (
              <Badge count={alertCount} style={{ marginLeft: 8 }} />
            )}
          </div>
        </div>
        <div className="performance-dashboard-actions">
          <Space>
            <Tooltip title={isAutoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh'}>
              <Button
                icon={isAutoRefresh ? <PauseOutlined /> : <PlayCircleOutlined />}
                onClick={handleToggleAutoRefresh}
              />
            </Tooltip>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading.all}
            >
              Refresh
            </Button>
            <Button
              icon={<SettingOutlined />}
              onClick={handleSettingsUpdate}
            >
              Settings
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportData}
            >
              Export
            </Button>
          </Space>
        </div>
      </div>

      {/* Last Updated */}
      {lastUpdated && (
        <div className="performance-dashboard-last-updated">
          Last updated: {new Date(lastUpdated).toLocaleString()}
        </div>
      )}

      {/* Performance Metrics Cards */}
      <div className="performance-dashboard-metrics">
        <Row gutter={[16, 16]}>
          {performanceMetrics.map((metric, index) => (
            <Col xs={24} sm={12} md={8} lg={6} xl={4} key={index}>
              <MetricCard
                title={metric.title}
                value={metric.value}
                unit={metric.unit}
                status={metric.status}
                tooltip={metric.tooltip}
                icon={metric.icon}
                loading={loading.performance}
              />
            </Col>
          ))}
        </Row>
      </div>

      {/* Main Content Grid */}
      <div className="performance-dashboard-content">
        <Row gutter={[16, 16]}>
          {/* Health Status */}
          <Col xs={24} lg={12}>
            <HealthStatus
              healthData={health}
              loading={loading.health}
            />
          </Col>

          {/* System Resources */}
          <Col xs={24} lg={12}>
            <SystemResources
              resourceData={resources}
              loading={loading.resources}
            />
          </Col>

          {/* Performance Charts */}
          <Col xs={24}>
            <PerformanceCharts
              performanceData={performance}
              loading={loading.performance}
            />
          </Col>

          {/* Performance Alerts */}
          <Col xs={24}>
            <PerformanceAlerts
              performanceData={performance}
              healthData={health}
              settings={{
                alertThresholds: {
                  eventLoopLatency: 100,
                  memoryUsage: 80,
                  cpuLoad: 5.0,
                },
              }}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
});

PerformanceDashboard.displayName = 'PerformanceDashboard';

export default PerformanceDashboard;
